import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Switch, Button, Card, message, Spin, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getWebhookSubscription,
  createWebhookSubscription,
  updateWebhookSubscription,
  WEBHOOK_EVENT_TYPES,
} from '@/api/integrationApi';
import IntegrationNav from './IntegrationNav';

export default function WebhookSubscriptionForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getWebhookSubscription(id);
      form.setFieldsValue({
        name: data.name,
        target_url: data.target_url,
        secret: '', // never populate masked value
        event_types: data.event_types,
        is_active: data.is_active,
        description: data.description,
        headers_json: data.headers && Object.keys(data.headers).length > 0
          ? JSON.stringify(data.headers, null, 2)
          : '',
        max_retries: data.max_retries,
        retry_delay_seconds: data.retry_delay_seconds,
      });
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      let headers: Record<string, string> | undefined;
      const headersJson = values.headers_json as string;
      if (headersJson?.trim()) {
        try {
          headers = JSON.parse(headersJson);
        } catch {
          message.error(t('webhook.invalid_headers_json'));
          setSaving(false);
          return;
        }
      }

      if (isEdit) {
        const update: Record<string, unknown> = {
          name: values.name,
          target_url: values.target_url,
          event_types: values.event_types,
          is_active: values.is_active,
          description: values.description,
          headers,
          max_retries: values.max_retries,
          retry_delay_seconds: values.retry_delay_seconds,
        };
        if (values.secret) update.secret = values.secret;
        await updateWebhookSubscription(id!, update);
      } else {
        await createWebhookSubscription({
          name: values.name as string,
          target_url: values.target_url as string,
          secret: values.secret as string,
          event_types: values.event_types as string[],
          is_active: values.is_active as boolean,
          description: (values.description as string) || '',
          headers,
          max_retries: values.max_retries as number,
          retry_delay_seconds: values.retry_delay_seconds as number,
        });
      }
      message.success(t('webhook.subscription_saved'));
      navigate('/integration/webhooks');
    } catch {
      message.error(t('app.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <IntegrationNav />
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/integration/webhooks')}
        style={{ marginBottom: 16 }}
      >
        {t('app.back')}
      </Button>

      <Card title={isEdit ? t('webhook.edit_subscription_title') : t('webhook.create_subscription_title')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            max_retries: 5,
            retry_delay_seconds: 60,
            event_types: [],
          }}
        >
          <Form.Item
            name="name"
            label={t('webhook.field.name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="target_url"
            label={t('webhook.field.target_url')}
            rules={[{ required: true }, { type: 'url' }]}
          >
            <Input placeholder="https://example.com/webhook" />
          </Form.Item>

          <Form.Item
            name="secret"
            label={t('webhook.field.secret')}
            rules={isEdit ? [] : [{ required: true }]}
            extra={isEdit ? t('webhook.secret_edit_hint') : t('webhook.secret_hint')}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="event_types"
            label={t('webhook.field.event_types')}
            rules={[{ required: true, type: 'array', min: 1, message: t('webhook.event_types_required') }]}
          >
            <Select
              mode="multiple"
              placeholder={t('webhook.select_event_types')}
              options={WEBHOOK_EVENT_TYPES.map((et) => ({
                value: et,
                label: et === '*' ? t('webhook.event_type_all') : et,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('webhook.field.is_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('webhook.field.description')}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="headers_json"
            label={t('webhook.field.headers')}
            extra={t('webhook.headers_hint')}
          >
            <Input.TextArea rows={3} placeholder='{"X-Custom-Header": "value"}' />
          </Form.Item>

          <Space>
            <Form.Item
              name="max_retries"
              label={t('webhook.field.max_retries')}
            >
              <InputNumber min={0} max={10} />
            </Form.Item>

            <Form.Item
              name="retry_delay_seconds"
              label={t('webhook.field.retry_delay_seconds')}
            >
              <InputNumber min={10} max={3600} />
            </Form.Item>
          </Space>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
